import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus, Search, Send } from 'lucide-react';
import { mozoApi, mensajeDeError } from './mozoApi';
import { claveNueva, money } from './cobroUtils';
import useRestaurantePedidoCart from '../hooks/restaurante/useRestaurantePedidoCart';

const normalizar = (texto) =>
  String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export default function MozoPedido({ sesionId, cuentaId, enLinea, onEnviado }) {
  const carrito = useRestaurantePedidoCart();
  const [catalogo, setCatalogo] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [errorCatalogo, setErrorCatalogo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  // La misma clave se reutiliza al reintentar: si el primer envío sí llegó,
  // el servidor devuelve ese pedido en vez de crear otro.
  const claveEnvio = useRef(null);

  useEffect(() => {
    let activo = true;

    mozoApi
      .get('/restaurante/catalogo')
      .then(({ data }) => activo && setCatalogo(Array.isArray(data) ? data : []))
      .catch((err) => activo && setErrorCatalogo(mensajeDeError(err, 'No se pudo cargar la carta.')));

    return () => {
      activo = false;
    };
  }, []);

  const resultados = useMemo(() => {
    const q = normalizar(busqueda.trim());

    if (!q) return catalogo.slice(0, 30);

    return catalogo
      .filter((p) => normalizar(`${p.nombre} ${p.codigo || ''}`).includes(q))
      .slice(0, 30);
  }, [catalogo, busqueda]);

  const enviar = async () => {
    if (enviando || !carrito.items.length || !enLinea) return;

    if (!claveEnvio.current) claveEnvio.current = claveNueva();

    setEnviando(true);
    setError('');

    try {
      await mozoApi.post('/restaurante/pedidos/enviar', {
        sesion_id: sesionId,
        cuenta_id: cuentaId,
        clave_idempotencia: claveEnvio.current,
        items: carrito.items.map((item) => ({
          producto_id: item.id,
          cantidad: Number(item.cantidad),
          observaciones: String(item.observaciones || '').trim() || null,
        })),
      });

      claveEnvio.current = null;
      carrito.vaciar();
      setBusqueda('');
      onEnviado?.();
    } catch (err) {
      const datos = err?.response?.data;

      // Un rechazo del servidor (por ejemplo, sin stock) no se reintenta
      // igual: se cambia la clave. Un corte de red conserva la clave.
      if (err?.response) claveEnvio.current = null;

      setError(
        datos?.producto?.nombre && datos?.producto?.stock_disponible !== undefined
          ? `Stock insuficiente de ${datos.producto.nombre}. Disponible: ${datos.producto.stock_disponible}.`
          : mensajeDeError(err, 'No se pudo enviar el pedido.')
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="relative">
        <Search size={18} className="absolute left-3 top-3.5 text-gray-400" />
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar producto"
          className="w-full rounded-xl border border-gray-300 py-3 pl-10 pr-3 text-base focus:border-green-600 focus:outline-none"
        />
      </div>

      {errorCatalogo && <p className="mt-2 text-sm font-bold text-red-600">{errorCatalogo}</p>}

      <ul className="mt-3 divide-y divide-gray-100 rounded-xl bg-white shadow-sm">
        {resultados.map((producto) => (
          <li key={producto.id}>
            <button
              type="button"
              onClick={() => carrito.agregar(producto)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left active:bg-green-50"
            >
              <span className="text-base font-semibold text-gray-900">{producto.nombre}</span>
              <span className="whitespace-nowrap text-sm font-bold text-green-800">
                {money(producto.precio)}
              </span>
            </button>
          </li>
        ))}
        {!resultados.length && !errorCatalogo && (
          <li className="px-4 py-6 text-center text-sm text-gray-500">Sin resultados.</li>
        )}
      </ul>

      {carrito.items.length > 0 && (
        <section className="mt-5 rounded-2xl bg-white p-4 shadow-md">
          <h3 className="text-sm font-extrabold uppercase text-gray-600">Pedido a enviar</h3>

          <ul className="mt-2 space-y-3">
            {carrito.items.map((item) => (
              <li key={item.id}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-bold text-gray-900">{item.nombre}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label={`Quitar uno de ${item.nombre}`}
                      onClick={() => carrito.cambiarCantidad(item.id, -1)}
                      className="rounded-lg bg-gray-100 p-2"
                    >
                      <Minus size={18} />
                    </button>
                    <span className="w-6 text-center text-lg font-extrabold">{item.cantidad}</span>
                    <button
                      type="button"
                      aria-label={`Agregar uno de ${item.nombre}`}
                      onClick={() => carrito.cambiarCantidad(item.id, 1)}
                      className="rounded-lg bg-gray-100 p-2"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={item.observaciones}
                  maxLength={200}
                  onChange={(e) => carrito.cambiarObservaciones(item.id, e.target.value)}
                  placeholder="Observaciones (sin cebolla, punto...)"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </li>
            ))}
          </ul>

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
              {error}
            </p>
          )}

          {!enLinea && (
            <p className="mt-3 text-sm font-bold text-red-700">
              Sin conexión: no se puede enviar hasta que vuelva el wifi.
            </p>
          )}

          <button
            type="button"
            onClick={enviar}
            disabled={enviando || !enLinea}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-green-700 py-4 text-base font-extrabold text-white disabled:opacity-50"
          >
            <Send size={18} />
            {enviando ? 'Enviando...' : `Enviar a cocina · ${money(carrito.total)}`}
          </button>
        </section>
      )}
    </div>
  );
}
